import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, CheckIcon, UserIcon, UsersIcon, HomeIcon, PawPrintIcon, CarIcon, CreditCardIcon, CalendarIcon, LockIcon, Building2Icon, ServerIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { RelationshipPathways } from '../components/card/RelationshipPathways';
import { EcosystemFoundation } from '../components/card/EcosystemFoundation';
import { AccessMethods } from '../components/card/AccessMethods';
import { trackEvent as track } from '../utils/analytics';
const FR: Record<string, string> = {
  Family: 'Famille',
  Properties: 'Propriétés',
  Vehicles: 'Véhicules',
  Memberships: 'Adhésions',
  Licenses: 'Permis',
  Subscriptions: 'Abonnements',
  Loyalty: 'Fidélité',
  'Access Rights': "Droits d'accès",
  Person: 'Personne',
  Property: 'Propriété',
  Vehicle: 'Véhicule',
  Animal: 'Animal',
  License: 'Permis',
  Membership: 'Adhésion',
  Subscription: 'Abonnement',
  Benefits: 'Avantages',
  'Family Plans': 'Forfaits familiaux',
  'Shared Memberships': 'Adhésions partagées',
  'Resident Pricing': 'Tarif résident',
  'Parking Access': 'Accès au stationnement',
  'Licenses & Permits': 'Permis et licences',
  'Loyalty & Rewards': 'Fidélité et récompenses',
  Assets: 'Actifs',
  Citizen: 'Citoyen',
  Student: 'Étudiant',
  Traveler: 'Voyageur',
  Customer: 'Client',
  Household: 'Foyer',
  Dependents: 'Personnes à charge',
  Companions: 'Accompagnateurs',
  'Family Account': 'Compte familial',
  'Property / Vehicle': 'Propriété / Véhicule',
  Devices: 'Appareils',
  'Luggage / Vehicle': 'Bagages / Véhicule',
  'Registered Products': 'Produits enregistrés',
  'Resident Status': 'Statut de résident',
  Enrollment: 'Inscription',
  'Frequent Flyer': 'Grand voyageur',
  'Loyalty Tier': 'Niveau de fidélité',
  Subsidies: 'Subventions',
  Grants: 'Bourses',
  'Lounge Access': 'Accès au salon',
  Rewards: 'Récompenses',
  Facilities: 'Installations',
  'Campus Buildings': 'Bâtiments du campus',
  'Fast Track': 'Voie rapide',
  'Exclusive Events': 'Événements exclusifs',
  'Download App': "Télécharger l'appli",
  'Activate Card': 'Activer la carte',
  'Verify Identity': "Vérifier l'identité",
  'Connect Relationships': 'Connecter les relations',
  'Access Services': 'Accéder aux services',
  'Receive Benefits': 'Recevoir les avantages',
  'Use ATNA Experiences': 'Utiliser les expériences ATNA',
  'NFC Access': 'Accès NFC',
  'Membership Validation': "Validation d'adhésion",
  'Resident Verification': 'Vérification de résidence',
  'Discount Programs': 'Programmes de rabais',
  'Loyalty Programs': 'Programmes de fidélité',
  'Family Accounts': 'Comptes familiaux'
};
const DEMO_FR: Record<string, string> = {
  Household: 'Foyer',
  '2 members': '2 membres',
  Vehicle: 'Véhicule',
  'Registration active': 'Immatriculation active'
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const RELATIONSHIP_NODES = [{
  Icon: UserIcon,
  en: 'Person',
  fr: 'Personne'
}, {
  Icon: UsersIcon,
  en: 'Family',
  fr: 'Famille'
}, {
  Icon: HomeIcon,
  en: 'Property',
  fr: 'Propriété'
}, {
  Icon: PawPrintIcon,
  en: 'Pet',
  fr: 'Animal'
}, {
  Icon: CarIcon,
  en: 'Vehicle',
  fr: 'Véhicule'
}, {
  Icon: CreditCardIcon,
  en: 'Membership',
  fr: 'Adhésion'
}, {
  Icon: CalendarIcon,
  en: 'Subscription',
  fr: 'Abonnement'
}, {
  Icon: LockIcon,
  en: 'Rights',
  fr: 'Droits'
}];
const VALUE_CARDS = [{
  title: 'Family Plans',
  subtitle: 'Service + Transaction'
}, {
  title: 'Shared Memberships',
  subtitle: 'Service + Transaction'
}, {
  title: 'Resident Pricing',
  subtitle: 'Service + Transaction'
}, {
  title: 'Parking Access',
  subtitle: 'Service + Transaction'
}, {
  title: 'Licenses & Permits',
  subtitle: 'Service + Transaction'
}, {
  title: 'Loyalty & Rewards',
  subtitle: 'Service + Transaction'
}];
const COMPARISON_ROWS = [{
  label: 'Person',
  cities: 'Citizen',
  associations: 'Member',
  universities: 'Student',
  airports: 'Traveler',
  retail: 'Customer'
}, {
  label: 'Family',
  cities: 'Household',
  associations: 'Family Plan',
  universities: 'Dependents',
  airports: 'Companions',
  retail: 'Family Account'
}, {
  label: 'Assets',
  cities: 'Property / Vehicle',
  associations: 'Equipment',
  universities: 'Devices',
  airports: 'Luggage / Vehicle',
  retail: 'Registered Products'
}, {
  label: 'Membership',
  cities: 'Resident Status',
  associations: 'Tier Level',
  universities: 'Enrollment',
  airports: 'Frequent Flyer',
  retail: 'Loyalty Tier'
}, {
  label: 'Benefits',
  cities: 'Subsidies',
  associations: 'Discounts',
  universities: 'Grants',
  airports: 'Lounge Access',
  retail: 'Rewards'
}, {
  label: 'Access Rights',
  cities: 'Facilities',
  associations: 'Events',
  universities: 'Campus Buildings',
  airports: 'Fast Track',
  retail: 'Exclusive Events'
}];
export function DigitalCard() {
  const {
    lang
  } = useLanguage();
  const t = (en: string) => lang === 'FR' ? FR[en] ?? en : en;
  const demoT = (en: string) => lang === 'FR' ? DEMO_FR[en] ?? en : en;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Digital Card'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8593" data-mp="1sopfjbfucg0s">
      <Header data-id="element-8594" data-mp="1spzd7zis7m6w" />

      <main className="pt-[120px]" data-id="element-8595" data-mp="1srjzl1uxzyto">
        {/* HERO SECTION */}
        <section className="pt-24 pb-32 px-6" data-id="element-8596" data-mp="1xnslcy3naq9t">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-8597" data-mp="1ejfbroox5k43">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-8598" data-mp="19t8phvhvqbp0">
              <div className="font-sans text-xs font-semibold tracking-[0.22em] text-[#121212] uppercase mb-6" data-id="element-8599" data-mp="1xi9s32416smd">
                {lang === 'FR' ? 'Identité et carte numérique' : 'Digital identity & card'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-[1.08] tracking-tight mb-8" data-id="element-8600" data-mp="1niiwsy7nc9l8">
                <span className="block" data-id="element-8601" data-mp="1indesahd7f2f">
                  {lang === 'FR' ? 'Une identité.' : 'One identity.'}
                </span>
                <span className="block" data-id="element-8602" data-mp="75lyoq7nw6so">
                  {lang === 'FR' ? 'Toutes vos relations connectées.' : 'Every relationship connected.'}
                </span>
              </h1>
              <p className="font-sans text-lg lg:text-xl text-[#5B6478] leading-relaxed mb-7 max-w-xl" data-id="element-8603" data-mp="2ntbtamfve6hk">
                {lang === 'FR' ? 'Votre carte numérique réunit identité, relations, droits et services dans une expérience de confiance.' : 'Your digital card brings identity, relationships, rights and services together — in one trusted experience.'}
              </p>
              <p className="font-sans text-sm text-[#121212]/45 leading-relaxed mb-7 max-w-xl" data-id="element-8604" data-mp="36hgrledrcz8z">
                {lang === 'FR' ? 'Famille · Adhésions · Propriétés · Animaux · Véhicules · Abonnements · Droits d’accès' : 'Family · Memberships · Properties · Pets · Vehicles · Subscriptions · Access rights'}
              </p>
              <p className="font-serif text-xl text-[#121212] leading-snug mb-10 max-w-xl" data-id="element-8605" data-mp="1m2ojzelovcrk">
                {lang === 'FR' ? 'Une seule fondation. Adaptable à tous les écosystèmes que vous desservez.' : 'One foundation. Adaptable to every ecosystem you serve.'}
              </p>
              <div className="flex flex-wrap items-center gap-6" data-id="element-8606" data-mp="1k4b7zj29hkp">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Digital Card Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-8607" data-mp="3srgx2ruw2j6y">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8608" data-mp="u9tdeg7ibnnw" />
                </Link>
                <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#121212] hover:text-indigo transition-colors" data-id="element-8609" data-mp="ubnx5r71l9qe">
                  {lang === 'FR' ? 'Découvrir Brossard' : 'Explore Brossard'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-8610" data-mp="11qzpd4cj6qy7" />
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative flex justify-center items-center h-[500px]" data-id="element-8611" data-mp="ruwpy692d6ey">
              <img src={lang === 'FR' ? 'https://cdn.magicpatterns.com/uploads/uHyx7j3K2c4rYJ9FYAKX8r/ChatGPT_Image_7_juil._2026,_13_42_38.png' : 'https://cdn.magicpatterns.com/uploads/oyN36MaM75956RY7Hf1rfF/ChatGPT_Image_7_juil._2026,_13_38_42.png'} alt={lang === 'FR' ? 'Application Carte numérique BCITI reliée à la famille, aux propriétés, aux véhicules, aux animaux, aux permis, aux abonnements, aux adhésions et aux droits d’accès' : 'BCITI Digital Card app connected to family, properties, vehicles, pets, licences, subscriptions, memberships and access rights'} className="w-full h-auto object-contain max-w-md mx-auto" data-id="element-8612" data-mp="1atimr8xjsbd2" />
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: CARDS IDENTIFY. BCITI CARD UNDERSTANDS. */}
        <section className="py-24 lg:py-28 px-6 border-t border-[#E4E6EC]" data-id="element-8613" data-mp="2qbw0jw6ayzh1">
          <div className="max-w-[1320px] mx-auto" data-id="element-8614" data-mp="2vd37xu4j9wn6">
            <motion.div {...reveal} className="text-center max-w-3xl mx-auto" data-id="element-8615" data-mp="2hcipiz2j62u7">
              <p className="font-sans text-xs font-semibold tracking-[0.24em] text-indigo uppercase" data-id="element-8616" data-mp="2ldkpji49uix8">
                {lang === 'FR' ? 'Une approche différente' : 'A different approach'}
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#121212] leading-[1.12] tracking-tight mt-5" data-id="element-8617" data-mp="1xsk3bpc9xs3o">
                {lang === 'FR' ? <>
                    <span className="block" data-id="element-8618" data-mp="26rf7kvvrcs4c">Les cartes identifient.</span>
                    <span className="block" data-id="element-8619" data-mp="2lp7p1tkv7ql6">La carte numérique BCITI comprend.</span>
                  </> : 'Cards identify. The BCITI Digital Card understands.'}
              </h2>
              <p className="font-sans text-lg text-[#5B6478] mt-5" data-id="element-8620" data-mp="2uhri8eie12ym">
                {lang === 'FR' ? 'La même simplicité. Une compréhension plus riche des relations.' : 'Same simplicity. A deeper relationship view.'}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-14" data-id="element-8621" data-mp="2zhdtiieno94s">
              {/* Traditional card */}
              <motion.div {...reveal} className="rounded-2xl bg-[#F6F7F9] px-8 py-10 lg:px-10 flex flex-col sm:flex-row items-center gap-8 lg:gap-10" data-id="element-8622" data-mp="1x4hkxdzlzlat">
                <div className="w-[200px] h-[126px] shrink-0 rounded-xl bg-white flex items-center gap-4 px-5 shadow-[0_1px_4px_rgba(18,18,18,0.06)]" data-id="element-8623" data-mp="2632zky1ymg6b">
                  <span className="w-9 h-7 rounded-[5px] bg-[#D8DAE0]" aria-hidden="true" data-id="element-8624" data-mp="2fto8pff9f33h" />
                  <span className="font-sans text-[11px] tracking-[0.16em] uppercase text-[#5B6478]" data-id="element-8625" data-mp="2krfwpycsa52e">
                    {lang === 'FR' ? 'Carte traditionnelle' : 'Traditional card'}
                  </span>
                </div>
                <div data-id="element-8626" data-mp="2trcqufhxzw4u">
                  <p className="font-sans text-[11px] font-semibold tracking-[0.18em] uppercase text-[#5B6478]" data-id="element-8627" data-mp="ugctkzvp4g0r">
                    {lang === 'FR' ? 'Une carte traditionnelle' : 'A traditional card'}
                  </p>
                  <h3 className="font-serif text-2xl lg:text-3xl text-[#121212] leading-snug mt-4" data-id="element-8628" data-mp="rx7zwug9x80v">
                    {lang === 'FR' ? <>
                        <span className="block" data-id="element-8629" data-mp="i757avd2dw32">Une carte traditionnelle</span>
                        <span className="block" data-id="element-8630" data-mp="d7itajpewbt7">vous identifie.</span>
                      </> : <>
                        <span className="block" data-id="element-8631" data-mp="1atzpflmesjdd">A traditional card</span>
                        <span className="block" data-id="element-8632" data-mp="15wppvd5b2rbt">identifies you.</span>
                      </>}
                  </h3>
                  <p className="flex items-center gap-3 font-sans text-base text-[#5B6478] mt-5" data-id="element-8633" data-mp="10yo94yvcr43i">
                    <ArrowRightIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8634" data-mp="wx2p8tkehpjt" />
                    {lang === 'FR' ? 'Qui êtes-vous ?' : 'Who are you?'}
                  </p>
                </div>
              </motion.div>

              {/* BCITI card */}
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.08
            }} className="rounded-2xl bg-[#F4F4FB] px-8 py-10 lg:px-10 flex flex-col sm:flex-row items-center gap-8 lg:gap-10 overflow-hidden" data-id="element-8635" data-mp="1e4aoz0h5it26">
                <img src={lang === 'FR' ? 'https://cdn.magicpatterns.com/uploads/eqNnXTaJtf8zAL99M7ppDD/ChatGPT_Image_7_juil._2026,_13_50_15.png' : 'https://cdn.magicpatterns.com/uploads/28jQomEgNYp1hxpV8x7trp/ChatGPT_Image_7_juil._2026,_13_54_09.png'} alt={lang === 'FR' ? 'Application Carte numérique BCITI — identité numérique du citoyen avec ses liens connectés : famille, véhicule, propriété, adhésion et abonnement' : 'BCITI Digital Card app — digital resident identity with connected relationships: household, vehicle, property, membership and subscription'} className="w-[200px] h-auto shrink-0" data-id="element-8636" data-mp="1s3tkmtss0bxl" />
                <div data-id="element-8637" data-mp="94m08r5j7h4o">
                  <p className="font-sans text-[11px] font-semibold tracking-[0.18em] uppercase text-indigo" data-id="element-8638" data-mp="95dtvwfkj4sb">
                    {lang === 'FR' ? 'Carte numérique BCITI' : 'BCITI Digital Card'}
                  </p>
                  <h3 className="font-serif text-2xl lg:text-3xl text-[#121212] leading-snug mt-4" data-id="element-8639" data-mp="3cmma6bundwkx">
                    {lang === 'FR' ? <>
                        <span className="block" data-id="element-8640" data-mp="3miz1j32pzyci">La carte numérique BCITI</span>
                        <span className="block" data-id="element-8641" data-mp="2ytei6j2nmz2p">comprend vos relations.</span>
                      </> : <>
                        <span className="block" data-id="element-8642" data-mp="2tu1u5v01rlyj">The BCITI Digital Card</span>
                        <span className="block" data-id="element-8643" data-mp="3jhnuhjjx6s1z">understands your relationships.</span>
                      </>}
                  </h3>
                  <p className="flex items-center gap-3 font-sans text-base text-indigo mt-5" data-id="element-8644" data-mp="3nhy0uxbmjkhd">
                    <ArrowRightIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8645" data-mp="3sddb7fafay91" />
                    {lang === 'FR' ? 'À qui et à quoi êtes-vous relié ?' : 'Who and what are you connected to?'}
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Relationship network */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.12
          }} className="flex flex-wrap justify-center items-start gap-x-1 gap-y-8 mt-12" data-id="element-8646" data-mp="3el75kn4wc9nu">
              {RELATIONSHIP_NODES.map((node, i, arr) => <Fragment key={node.en} data-id="element-8647" data-mp="2edigs69cjqmx" data-mpi={i}>
                  <div className="flex flex-col items-center w-[84px] sm:w-[96px]" data-id="element-8648" data-mp="29bt7ow5opn1h">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${i === 0 ? 'bg-indigo/10 text-indigo' : 'bg-[#F6F7F9] text-[#121212]'}`} data-id="element-8649" data-mp="24lcrj4ahbnbf">
                      <node.Icon className="w-5 h-5" strokeWidth={1.6} data-id="element-8650" data-mp="1vl7wltmh2he5" />
                    </div>
                    <span className="font-sans text-sm text-[#5B6478] text-center leading-snug" data-id="element-8651" data-mp="1qllizj2qez4t">
                      {lang === 'FR' ? node.fr : node.en}
                    </span>
                  </div>
                  {i < arr.length - 1 && <div className="hidden lg:block w-10 h-px bg-[#E4E6EC] mt-6" aria-hidden="true" data-id="element-8652" data-mp="1gprxvbay5xbf" />}
                </Fragment>)}
            </motion.div>

            {/* Closing statement */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.16
          }} className="text-center mt-14" data-id="element-8653" data-mp="2jx4b40aq5duf">
              <p className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-8654" data-mp="2b1y1src8wdn5">
                {lang === 'FR' ? <>
                    <span className="block" data-id="element-8655" data-mp="215l6jp11xrqa">L’identité devient contexte.</span>
                    <span className="block" data-id="element-8656" data-mp="264o1ggnbihkj">
                      Le contexte rend les services plus intelligents.
                    </span>
                  </> : <>
                    <span className="block" data-id="element-8657" data-mp="1w68f0qz8jwhd">Identity becomes context.</span>
                    <span className="block" data-id="element-8658" data-mp="3mj7jb8btcvlt">
                      Context makes services smarter.
                    </span>
                  </>}
              </p>
              <div className="w-14 h-[2px] bg-indigo mx-auto mt-7" aria-hidden="true" data-id="element-8659" data-mp="3qioos1r8psqz" />
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: FROM RELATIONSHIPS TO REAL SERVICES */}
        <section className="py-24 lg:py-28 px-6 border-t border-[#E4E6EC]" data-id="element-8660" data-mp="1ooahlkubwbui">
          <RelationshipPathways data-id="element-8661" data-mp="1jwsd4mt3jt4s" />
        </section>

        {/* SECTION 4: DIFFERENT ECOSYSTEMS. SAME FOUNDATION. */}
        <section className="py-24 lg:py-28 px-6 border-t border-[#E4E6EC]" data-id="element-8662" data-mp="1ykn706iuc3jg">
          <EcosystemFoundation data-id="element-8663" data-mp="1tmlphq12cca3" />
        </section>

        {/* SECTION 5: PROVEN IN PRODUCTION */}
        <section className="py-16 lg:py-20 px-6 border-t border-[#E4E6EC]" data-id="element-8664" data-mp="27luurunqdlza">
          <motion.div {...reveal} className="max-w-[1320px] mx-auto rounded-2xl border border-[#E7E8F0] bg-white px-7 lg:px-10 pt-10 pb-8 relative" data-id="element-8665" data-mp="27jqsfh5hcv14">
            <span className="absolute -top-3.5 left-7 lg:left-10 bg-indigo text-white rounded-md font-sans text-[11px] font-semibold tracking-[0.16em] uppercase px-3.5 py-1.5" data-id="element-8666" data-mp="da6d3kq475b7">
              {lang === 'FR' ? 'Éprouvé en production' : 'Proven in production'}
            </span>

            <h2 className="font-serif text-4xl md:text-5xl text-[#121212] leading-tight tracking-tight" data-id="element-8667" data-mp="37q1lav6o1rpy">
              {lang === 'FR' ? 'Déployé. Utilisé. Éprouvé.' : 'Deployed. Used. Proven.'}
            </h2>
            <p className="font-sans text-lg text-[#5B6478] mt-3" data-id="element-8668" data-mp="2tr20jym9xwz2">
              {lang === 'FR' ? 'Ville de Brossard. Une plateforme au service de communautés réelles.' : 'City of Brossard. A platform serving real communities.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-0 sm:divide-x sm:divide-[#EDEEF3] mt-9" data-id="element-8669" data-mp="32pnefggswnti">
              {[{
              Icon: UsersIcon,
              value: lang === 'FR' ? '88 266' : '88,266',
              label: lang === 'FR' ? 'Usagers actifs' : 'Active users'
            }, {
              Icon: CalendarIcon,
              value: lang === 'FR' ? 'Depuis 2017' : 'Since 2017',
              label: lang === 'FR' ? 'En production' : 'In production'
            }, {
              Icon: Building2Icon,
              value: lang === 'FR' ? 'Plus de 20' : '20+',
              label: lang === 'FR' ? 'Communautés' : 'Communities'
            }].map(({
              Icon,
              value,
              label
            }) => <div key={label} className="flex items-center gap-4 sm:px-8 first:sm:pl-0" data-id="element-8670" data-mp="1987yalze7chr">
                  <span className="w-12 h-12 shrink-0 rounded-full bg-[#F5F5FB] flex items-center justify-center" data-id="element-8671" data-mp="ziof1f7rpgmd">
                    <Icon className="w-5 h-5 text-indigo" strokeWidth={1.6} aria-hidden="true" data-id="element-8672" data-mp="1n8inzloyul1c" />
                  </span>
                  <span data-id="element-8673" data-mp="21ug55eemswze">
                    <span className="block font-serif text-2xl lg:text-[27px] text-[#121212] leading-tight" data-id="element-8674" data-mp="21upur0vrl24k">
                      {value}
                    </span>
                    <span className="block font-sans text-sm text-[#5B6478] mt-0.5" data-id="element-8675" data-mp="1rwjs2h24vryn">
                      {label}
                    </span>
                  </span>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 mt-8 pt-6 border-t border-[#EDEEF3]" data-id="element-8676" data-mp="1iy7utw6rhvx8">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-sans text-[15px] text-[#5B6478]" data-id="element-8677" data-mp="1e43nhbtl3lvm">
                <span className="inline-flex items-center gap-2.5" data-id="element-8678" data-mp="147qru8ehaxy8">
                  <LockIcon className="w-[18px] h-[18px] text-indigo" strokeWidth={1.6} aria-hidden="true" data-id="element-8679" data-mp="149k0ft8njs8v" />
                  {lang === 'FR' ? 'Conforme à la Loi 25' : 'Law 25 compliant'}
                </span>
                <span className="w-1 h-1 rounded-full bg-[#C9CBD6]" aria-hidden="true" data-id="element-8680" data-mp="36twadczmi5cq" />
                <span className="inline-flex items-center gap-2.5" data-id="element-8681" data-mp="3fvc6cfpoy4xf">
                  <ServerIcon className="w-[18px] h-[18px] text-indigo" strokeWidth={1.6} aria-hidden="true" data-id="element-8682" data-mp="3fszr4abbi8o2" />
                  {lang === 'FR' ? 'Infrastructure hébergée au Canada' : 'Infrastructure hosted in Canada'}
                </span>
              </div>

              <Link to="/brossard" className="inline-flex items-center gap-3 font-sans text-[15px] font-medium text-indigo hover:text-indigo/75 transition-colors duration-200 shrink-0" data-id="element-8683" data-mp="2x31k7xj9alm0">
                {lang === 'FR' ? 'Découvrir l’étude de cas de Brossard' : 'Explore the Brossard case study'}
                <ArrowRightIcon className="w-4 h-4" strokeWidth={1.75} data-id="element-8684" data-mp="fxcncfjhrcpz" />
              </Link>
            </div>
          </motion.div>
        </section>

        {/* SECTION 6: ONE IDENTITY. MULTIPLE WAYS TO ACCESS IT. */}
        <section className="py-24 lg:py-28 px-6 border-t border-[#E4E6EC]" data-id="element-8685" data-mp="oq65ckqboq7j">
          <AccessMethods data-id="element-8686" data-mp="jxmv9xqugd7h" />
        </section>

        {/* FINAL CTA */}
        <section className="py-32 lg:py-40 px-6 border-t border-[#E4E6EC]" data-id="element-8687" data-mp="ymixhe67yw06">
          <div className="max-w-3xl mx-auto text-center" data-id="element-8688" data-mp="yovm2a6hg4m7">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl lg:text-[56px] text-[#121212] leading-[1.1] tracking-tight mb-7" data-id="element-8689" data-mp="tmmvtlkzvgns">
              {lang === 'FR' ? 'Une identité. Toutes vos relations connectées.' : 'One identity. Every relationship connected.'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-lg lg:text-xl text-[#5B6478] mb-11 max-w-2xl mx-auto" data-id="element-8690" data-mp="35digsfzr4q6m">
              {lang === 'FR' ? 'Voyez comment BCITI | ATNA peut devenir la fondation de votre propre écosystème.' : 'See how BCITI | ATNA can become the foundation of your own ecosystem.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-8691" data-mp="2bkt6t7164t0o">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Digital Card Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-8692" data-mp="2gi1hkmjzlurw">
                {lang === 'FR' ? 'Demander une démo' : 'Request a demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8693" data-mp="3f942xhl6m0v" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-8694" data-mp="3qhm1q6q8nptc" />
    </div>;
}